import type { DraftState } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { PoolChoice, PoolTarget } from './pools.ts'
import { newPoolNumber, nextDropPool, placedGroups, poolChoices, withPoolNames } from './pools.ts'
import { poolNamesOf } from './sheet/sheetRoles.ts'
import { isArchiveOrder } from './archive.ts'

export type DropTarget =
  { kind: 'order' } | ({ kind: 'pool'; primary: boolean } & PoolChoice) | { kind: 'new'; pool: number; number: number }

/** Drops wait while the first files are read and while the order is created. */
export const dropDisabled = (draft: DraftState): boolean => draft.phase === 'detecting' || draft.phase === 'creating'

/** Where a drop can go: the order as a whole until it has items to pool, then by pool. */
export const poolTargetsApply = (draft: DraftState): boolean => !!draft.orderId && !isArchiveOrder(draft) && poolChoices(draft).length > 0

/** The drop targets for the draft: one for the whole order before it has items, else every pool (pool 1 the default) and a new pool. */
export const dropTargets = (draft: DraftState): DropTarget[] => {
  if (!poolTargetsApply(draft)) {
    return [{ kind: 'order' }]
  }
  const assigned = Object.fromEntries(placedGroups(draft).map((group) => [group.key, group.pool ?? 0] as const))
  const pools = withPoolNames(poolChoices(draft), poolNamesOf(draft, assigned))
  return [
    ...pools.map((pool, index): DropTarget => ({ kind: 'pool', primary: index === 0, ...pool })),
    { kind: 'new', pool: nextDropPool(draft), number: newPoolNumber(pools) }
  ]
}

/** The pool index a drop goes to; `undefined` for the first pool, where a drop goes without one. A new pool is resolved only now. */
export const resolveDropPool = (draft: DraftState, choice: PoolTarget | undefined): number | undefined => {
  if (choice === 'new') {
    return nextDropPool(draft)
  }
  const pool = choice ?? poolChoices(draft)[0]?.pool
  return pool ? pool : undefined
}

/** The pool a drop on `target` goes to, as `dropFiles` takes it. */
export const dropPoolFor = (draft: DraftState, target: DropTarget): number | undefined =>
  target.kind === 'order' ? undefined : resolveDropPool(draft, target.kind === 'new' ? 'new' : target.pool)
